import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Chip,
  Grid,
  Button,
  Divider
} from '@mui/material';
import { ArrowBack, LocalShipping, CheckCircle, Cancel } from '@mui/icons-material';
import { toast } from 'react-toastify';
import Header from '../components/common/Header';
import ordersService from '../services/orders.service';

const MyOrders = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const memoizedFetchOrders = useCallback(() => {
    fetchOrders();
  }, []);
  
  useEffect(() => {
    memoizedFetchOrders();
  }, [memoizedFetchOrders]);

  const fetchOrders = async () => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/auth');
        return;
      }

      const data = await ordersService.getMyOrders(token);
      setOrders(data);
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
      toast.error('Erreur lors du chargement des commandes');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!confirm('Voulez-vous vraiment annuler cette commande ?')) {
      return;
    }

    try {
      const token = localStorage.getItem('access_token');
      await ordersService.cancelOrder(orderId, token);
      toast.success('Commande annulée avec succès');
      fetchOrders(); // Recharger la liste
    } catch (error) {
      console.error('Erreur lors de l\'annulation:', error);
      toast.error(error.response?.data?.error || 'Erreur lors de l\'annulation');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return { bgcolor: '#FFA500', color: 'white' };
      case 'confirmed': return { bgcolor: '#2196F3', color: 'white' };
      case 'shipped': return { bgcolor: '#9C27B0', color: 'white' };
      case 'delivered': return { bgcolor: '#4CAF50', color: 'white' };
      case 'cancelled': return { bgcolor: '#F44336', color: 'white' };
      default: return { bgcolor: '#757575', color: 'white' };
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'En attente';
      case 'confirmed': return 'Confirmée';
      case 'shipped': return 'Expédiée';
      case 'delivered': return 'Livrée';
      case 'cancelled': return 'Annulée';
      default: return status;
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'delivered': return <CheckCircle />;
      case 'shipped': return <LocalShipping />;
      case 'cancelled': return <Cancel />;
      default: return null;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <Box className="mobile-app-container has-fixed-header"   sx={{ bgcolor: "#FFF7E5", minHeight: '100vh' }}>
        <Header />
        <Container sx={{ py: 4, textAlign: 'center' }}>
          <Typography>Chargement de vos commandes...</Typography>
        </Container>
      </Box>
    );
  }

  return (
    <Box className="mobile-app-container has-fixed-header"   sx={{ bgcolor: "#FFF7E5", minHeight: '100vh' }}>
      <Header />
      
      <Container sx={{ py: 4 }}>
        {/* En-tête */}
        <Button
          startIcon={<ArrowBack />}
          onClick={() => navigate('/')}
          sx={{ mb: 3, color: '#784e3c' }}
        >
          Retour à l'accueil
        </Button>

        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            fontFamily: 'Achiko',
            color: '#784e3c',
            mb: 1
          }}
        >
          📦 Mes Commandes
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
          Retrouvez l'historique de toutes vos commandes
        </Typography>

        {/* Liste des commandes */}
        {orders.length === 0 ? (
          <Card sx={{ textAlign: 'center', py: 8 }}>
            <CardContent>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Vous n'avez pas encore de commandes
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate('/articles')}
                sx={{
                  mt: 3,
                  bgcolor: '#784e3c',
                  fontFamily: 'Achiko',
                  '&:hover': { bgcolor: '#3a9bc7' }
                }}
              >
                Découvrir nos produits
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Grid container spacing={3}>
            {orders.map((order) => (
              <Grid item xs={12} key={order.id}>
                <Card sx={{ '&:hover': { boxShadow: 6 }, transition: 'all 0.3s ease' }}>
                  <CardContent>
                    <Grid container spacing={2}>
                      {/* Image du produit */}
                      <Grid item xs={12} sm={3}>
                        <img
                          src={order.product?.image_url || 'https://via.placeholder.com/200x200?text=Produit'}
                          alt={order.product?.title}
                          style={{
                            width: '100%',
                            height: '150px',
                            objectFit: 'cover',
                            borderRadius: '8px'
                          }}
                          onError={(e) => {
                            e.target.src = 'https://via.placeholder.com/200x200?text=Image+non+disponible';
                          }}
                        />
                      </Grid>

                      {/* Détails */}
                      <Grid item xs={12} sm={6}>
                        <Typography variant="h6" sx={{ fontFamily: 'Achiko', fontWeight: 'bold', mb: 1 }}>
                          {order.product?.title}
                        </Typography>
                        
                        <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                          <Chip
                            label={order.product?.product_type === 'physical' ? 'Physique' : ' Virtuel'}
                            size="small"
                          />
                          <Chip
                            icon={getStatusIcon(order.status)}
                            label={getStatusText(order.status)}
                            size="small"
                            sx={getStatusColor(order.status)}
                          />
                        </Box>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>Commandé le:</strong> {formatDate(order.ordered_at)}
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          <strong>Quantité:</strong> {order.quantity}
                        </Typography>

                        <Typography variant="h6" sx={{ color: '#784e3c', fontWeight: 'bold' }}>
                          {order.total_price} FCFA
                        </Typography>
                      </Grid>

                      {/* Actions */}
                      <Grid item xs={12} sm={3} sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 1 }}>
                        {order.status === 'pending' && (
                          <Button
                            variant="outlined"
                            color="error"
                            size="small"
                            onClick={() => handleCancelOrder(order.id)}
                            fullWidth
                          >
                            Annuler
                          </Button>
                        )}

                        {order.status === 'delivered' && order.product?.product_type === 'virtual' && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => {
                              if (order.product.virtual_type === 'video' && order.product.virtual_url) {
                                window.open(order.product.virtual_url, '_blank');
                              } else if (order.product.virtual_type === 'pdf' && order.product.pdf_url) {
                                window.open(order.product.pdf_url, '_blank');
                              }
                            }}
                            fullWidth
                            sx={{
                              bgcolor: '#4CAF50',
                              '&:hover': { bgcolor: '#45a049' }
                            }}
                          >
                            Accéder au contenu
                          </Button>
                        )}

                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => navigate(`/article/${order.product_id}`)}
                          fullWidth
                        >
                          Voir le produit
                        </Button>
                      </Grid>
                    </Grid>

                    {/* Adresse de livraison si produit physique */}
                    {order.product?.product_type === 'physical' && order.delivery_address && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                          <strong>Adresse de livraison:</strong>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {order.delivery_address}
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </Container>
    </Box>
  );
};

export default MyOrders;
